// Frontend OIDC plumbing (Phase 3b).
//
// Dual-mode login: the backend advertises AUTH_MODE via GET /api/auth/config.
// In mode 'local' the SPA keeps the legacy email/password form and this module
// stays dormant (no UserManager is ever constructed). In mode 'oidc' the login
// UI becomes a single "Sign in with SSO" button that runs the Authorization
// Code + PKCE flow against Keycloak; on return the access token is stashed in
// localStorage (via apiClient.setAuthToken) so the existing fetch interceptor
// sends it as `Authorization: Bearer` — same seam the legacy session used.
//
// oidc-client-ts owns the PKCE dance, token storage, and silent renew. We only
// mirror the access token into our own storage key and clean up the callback
// URL so the rest of the app (router, apiClient) is unchanged.

import { UserManager } from 'oidc-client-ts';
import { setAuthToken } from './apiClient.js';

// ── Config (fetched once, cached) ──────────────────────────────────────
let _configPromise = null;

// Fetch /api/auth/config a single time and memoize it. On any failure we fall
// back to { mode: 'local' } so a config outage never bricks login — the app
// simply shows the legacy form.
export function authConfig() {
  if (!_configPromise) {
    _configPromise = fetch('/api/auth/config')
      .then((r) => (r.ok ? r.json() : { mode: 'local' }))
      .catch(() => ({ mode: 'local' }));
  }
  return _configPromise;
}

// ── UserManager (constructed lazily, ONLY in oidc mode) ────────────────
let _um = null;

async function getUserManager() {
  if (_um) return _um;
  const cfg = await authConfig();
  if (cfg.mode !== 'oidc') return null; // never build a UserManager in local mode
  _um = new UserManager({
    authority: cfg.issuer,
    client_id: cfg.clientId || 'twin-frontend',
    redirect_uri: `${location.origin}/login`,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
  });
  return _um;
}

// ── Public API (consumed by LoginPage, main bootstrap, AppShell) ───────

// Kick off the redirect to Keycloak. No-op in local mode.
export async function login() {
  const um = await getUserManager();
  if (!um) return;
  await um.signinRedirect();
}

// Called ONCE at app bootstrap, before routes render. If the URL carries an
// OIDC `code`+`state` (i.e. we just came back from Keycloak), complete the
// exchange, mirror the access token into apiClient storage, scrub the query
// from the URL, and return true so the caller knows a login just landed.
// Returns false when there's nothing to handle (normal load, or local mode).
export async function handleCallbackIfPresent() {
  const params = new URLSearchParams(location.search);
  if (!params.has('code') || !params.has('state')) return false;
  const um = await getUserManager();
  if (!um) return false;
  const user = await um.signinRedirectCallback();
  setAuthToken(user.access_token);
  // Drop ?code&state so a reload/back doesn't replay the (now-consumed) code.
  history.replaceState({}, document.title, location.pathname);
  return true;
}

// Subscribe to silent-renew token refreshes. On each new token we re-mirror it
// into apiClient storage (so subsequent fetches carry the fresh Bearer) and
// notify `cb`. Returns an unsubscribe function. No-op unsubscribe in local mode.
export function onTokenRenewed(cb) {
  let um = null;
  const handler = (user) => {
    if (!user?.access_token) return;
    setAuthToken(user.access_token);
    cb(user.access_token);
  };
  getUserManager().then((m) => {
    if (!m) return;
    um = m;
    um.events.addUserLoaded(handler);
  });
  return () => {
    if (um) um.events.removeUserLoaded(handler);
  };
}

// End the session. Always clears the local token. In oidc mode with a live
// Keycloak session we also redirect through the provider's end-session endpoint
// (back to origin) so the SSO session itself is terminated — otherwise the next
// login would silently re-auth. In local mode there is nothing provider-side to
// do; the caller's own logout (session DELETE) already ran.
export async function logout() {
  setAuthToken(null);
  const um = await getUserManager();
  if (!um) return; // local mode
  const user = await um.getUser();
  if (user) {
    await um.signoutRedirect({ post_logout_redirect_uri: location.origin });
  }
}
